import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Car, 
  Calculator, 
  FileText, 
  ArrowRight, 
  CheckCircle2, 
  Zap, 
  Leaf, 
  Fuel,
  Building2,
  Shield,
  Clock
} from "lucide-react";

const LandingPage = ({ user, onLogout }) => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Calculator className="w-6 h-6" />,
      title: "Calcolo Automatico",
      description: "Calcola il fringe benefit in pochi click con le tabelle ACI aggiornate"
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Tabelle ACI 2026",
      description: "Database completo con oltre 4.600 veicoli delle principali marche"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Conforme alla Normativa",
      description: "Calcoli basati sulla Legge di Bilancio 2025 e tabelle ACI 2026"
    }
  ];

  const fuelTypes = [
    { type: "Full Electric", percentage: "10%", icon: <Zap className="w-5 h-5" />, color: "bg-emerald-100 text-emerald-700" },
    { type: "Plug-in Hybrid", percentage: "20%", icon: <Leaf className="w-5 h-5" />, color: "bg-blue-100 text-blue-700" },
    { type: "Benzina/Diesel", percentage: "50%", icon: <Fuel className="w-5 h-5" />, color: "bg-slate-100 text-slate-700" },
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope' }}>
                FringeCalculator
              </span>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              {user?.subscription_status === "active" ? (
                <>
                  <span className="text-slate-600 text-sm">Ciao, {user.azienda}</span>
                  <Button 
                    onClick={() => navigate('/calcola')}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="nav-calcola-btn"
                  >
                    Vai al Calcolatore
                  </Button>
                  <Button 
                    variant="ghost"
                    onClick={onLogout}
                    data-testid="logout-btn"
                  >
                    Esci
                  </Button>
                </>
              ) : (
                <>
                  <button 
                    onClick={() => navigate('/accedi')}
                    className="text-slate-600 hover:text-slate-900 transition-colors"
                    data-testid="nav-accedi"
                  >
                    Accedi
                  </button>
                  <Button 
                    onClick={() => navigate('/accedi')}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="nav-calcola-btn"
                  >
                    Inizia Ora
                  </Button>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-pattern py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <Clock className="w-4 h-4" />
                Aggiornato 2026
              </div>
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6"
                style={{ fontFamily: 'Manrope' }}
                data-testid="hero-title"
              >
                Calcola il{" "}
                <span className="text-blue-600">Fringe Benefit</span>{" "}
                della tua Auto Aziendale
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-xl">
                Strumento professionale per il calcolo del benefit fiscale delle auto aziendali 
                in uso promiscuo. Conforme alle tabelle ACI e alla normativa vigente.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-lg px-8 h-14"
                  onClick={() => navigate(user?.subscription_status === "active" ? '/calcola' : '/accedi')}
                  data-testid="hero-calcola-btn"
                >
                  {user?.subscription_status === "active" ? "Vai al Calcolatore" : "Inizia il Calcolo"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>

            {/* Right Content - Preview Card */}
            <div className="animate-slide-in-right">
              <Card className="calculator-card overflow-hidden">
                <CardContent className="p-0">
                  <div className="result-card p-6">
                    <p className="text-slate-400 text-sm mb-2">Fringe Benefit Annuo</p>
                    <p className="text-4xl font-bold number-display" data-testid="preview-amount">
                      € 2.039,02
                    </p>
                    <p className="text-slate-400 text-sm mt-2">€ 169,92 /mese</p>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Veicolo</span>
                      <span className="font-medium">VW Golf 1.5 TSI</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Alimentazione</span>
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded text-sm">
                        <Fuel className="w-4 h-4" />
                        Benzina
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Percentuale</span>
                      <span className="font-bold text-blue-600">50%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Costo km 15.000</span>
                      <span className="font-mono">€ 4.078,03</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Fuel Types Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
              Percentuali per Tipo di Alimentazione
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Dal 1° gennaio 2025, la percentuale per il calcolo del fringe benefit 
              dipende dal tipo di alimentazione del veicolo
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6" data-testid="fuel-types-grid">
            {fuelTypes.map((fuel, index) => (
              <Card 
                key={index} 
                className="card-hover border-2 border-slate-100"
                data-testid={`fuel-type-card-${index}`}
              >
                <CardContent className="p-6 text-center">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${fuel.color} mb-4`}>
                    {fuel.icon}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{fuel.type}</h3>
                  <p className="text-4xl font-bold text-blue-600 number-display">{fuel.percentage}</p>
                  <p className="text-sm text-slate-500 mt-2">del costo km × 15.000</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="flex gap-4 animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`feature-card-${index}`}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope' }}>
                Come Funziona il Calcolo
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-slate-900">Nuovo Ordine (dal 2025)</h4>
                    <p className="text-slate-600">
                      Per auto ordinate dal 1° gennaio 2025, si applica la percentuale 
                      basata sull'alimentazione (10%, 20% o 50%) sul costo chilometrico ACI a 15.000 km.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-slate-900">Riassegnazione (pre-2025)</h4>
                    <p className="text-slate-600">
                      Per auto immatricolate prima del 2025, si usa il "Valore Normale": 
                      Canone noleggio - Optional dipendente - (Km lavorativi × Costo km).
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <Card className="bg-slate-900 text-white border-0">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Building2 className="w-8 h-8 text-blue-400" />
                  <h3 className="text-xl font-semibold">Per le Aziende</h3>
                </div>
                <p className="text-slate-300 mb-6">
                  Gestisci facilmente il calcolo del fringe benefit per tutta la flotta aziendale. 
                  Accesso rapido alle tabelle ACI aggiornate e calcolo conforme alla normativa.
                </p>
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    Database ACI 2026 con 4.695 veicoli
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    Calcolo istantaneo e preciso
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    Supporto nuovo ordine e riassegnazione
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8" style={{ fontFamily: 'Manrope' }}>
            Pronto a Calcolare il Tuo Fringe Benefit?
          </h2>
          <Button 
            size="lg" 
            className="bg-blue-600 hover:bg-blue-700 text-lg px-10 h-14"
            onClick={() => navigate(user?.subscription_status === "active" ? '/calcola' : '/accedi')}
            data-testid="cta-calcola-btn"
          >
            {user?.subscription_status === "active" ? "Vai al Calcolatore" : "Calcola il Fringe Benefit"}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center">
                <Car className="w-4 h-4 text-white" />
              </div>
              <span className="text-slate-400">FringeCalculator</span>
            </div>
            <p className="text-slate-500 text-sm">
              © 2026 - Calcolo basato sulle tabelle ACI 2026 ufficiali
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
